import { useGetProperty } from "@app/property/hooks";
import { IProperty } from "@app/property/types";
import { useGetPropertyGroup } from "@app/propertyGroup/hooks";
import { IPropertyGroup } from "@app/propertyGroup/types";
import { useEffect, useMemo, useState } from "react";

import { PropertyProviderModes } from "../constants/PropertyProviderModes";
import { StorageKeys } from "../constants/StorageKeys";
import { configManager } from "../services/configuration/ConfigManager";
import { storageManager } from "../services/storageManager";
import { resolveNextActiveProperty } from "../utils/propertyUtils";
import { PropertyContext } from "./PropertyContext";

const configuredPropertyId = configManager.config.ibe.propertyId;
const configuredPropertyGroupId = configManager.config.ibe.propertyGroupId;

/**
 * Determine the property provider mode based on the configured property group id and the configured property id.
 * The property group id is leading, if it is set, then the property provider mode is multi-property.
 * If the property group id is not set, then the property provider mode falls back to single-property.
 */
const configuredPropertyProviderMode = !!configuredPropertyGroupId
    ? PropertyProviderModes.MultiProperty
    : PropertyProviderModes.SingleProperty;

export const PropertyContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [activePropertyId, setActivePropertyId] = useState<string | undefined>();
    const [hasStorageBeenRecovered, setHasStorageBeenRecovered] = useState<boolean>(false);
    const [recoveredActivePropertyId, setRecoveredActivePropertyId] = useState<string | undefined>();
    const [forceIbeReset, setForceIbeReset] = useState<boolean>(false);
    const [firstStep, setFirstStep] = useState<string>("search");
    const propertyGroupId = configuredPropertyGroupId;
    const [isInitialized, setIsInitialized] = useState<boolean>(false);
    const [mode] = useState<PropertyProviderModes>(configuredPropertyProviderMode);

    const { data: propertyGroupResponse, isFetched: isPropertyGroupFetched } = useGetPropertyGroup(propertyGroupId, {
        enabled: !!propertyGroupId,
    });

    const propertyGroup = propertyGroupResponse?.pages[0] || ({} as IPropertyGroup);
    const allProperties = propertyGroupResponse?.pages.map((item) => item.properties).flat() || [];

    const {
        data: activeProperty,
        isLoading: isActivePropertyLoading,
        isFetched: isActivePropertyFetched,
    } = useGetProperty(activePropertyId, { enabled: !!activePropertyId });

    /**
     * Responsible to recover previous state from storage
     * once the component is mounted
     */
    useEffect(() => {
        storageManager
            .getExtendedGlobalSession()
            .get(StorageKeys.ActivePropertyId)
            .then((id) => {
                setRecoveredActivePropertyId(id);

                setHasStorageBeenRecovered(true);
            });
    }, []);

    useEffect(() => {
        if (configuredPropertyId && mode === PropertyProviderModes.SingleProperty) {
            if (activePropertyId !== configuredPropertyId) {
                setActivePropertyId(configuredPropertyId);
            }
        }
    }, [configuredPropertyId, mode]);

    /**
     * Log error in case the active property id does not match the fetched property id
     */
    useEffect(() => {
        if (isActivePropertyFetched && activePropertyId !== activeProperty?.id) {
            console.error("HC PropertyContextProvider Error: Active property id does not match fetched property id");
        }
    }, [isActivePropertyFetched, activePropertyId, activeProperty]);

    useEffect(() => {
        if (activePropertyId) {
            storageManager.getExtendedGlobalSession().set(StorageKeys.ActivePropertyId, activePropertyId);
        }
    }, [activePropertyId]);

    /**
     * Helper function to resolve and set the next active property id
     */
    const resolveAndSetNextActiveProperty = () => {
        const { nextActivePropertyId, isRecoveredValueOverwritten, nextChildPage } = resolveNextActiveProperty({
            mode,
            propertyGroup,
            recoveredActivePropertyId,
            configuredPropertyId,
        });

        setActivePropertyId(nextActivePropertyId);

        if (isRecoveredValueOverwritten) {
            setForceIbeReset(true);
        }

        if (nextChildPage) {
            if (mode === PropertyProviderModes.SingleProperty) {
                setFirstStep("search");
            } else {
                setFirstStep(nextChildPage);
            }
        }
    };

    const changeActivePropertyId = (propertyId: string) => {
        setFirstStep("search");
        setActivePropertyId(propertyId);
        setForceIbeReset(true);
    };

    /**
     * Responsible to determine when this context provider is initialized.
     * Important:
     *  - it can only bet set to initialized once per lifecycle
     *  - the storage should be recovered since it could contain the previous active property id
     *  - mode related data should be available
     *      - if mode is "SingleProperty" then the activeProperty should be fetched
     *      - if mode is "MultiProperty" then the property group should be fetched. Active property id is no required
     */
    useEffect(() => {
        if (!isInitialized && hasStorageBeenRecovered && !!mode) {
            let nextIsInitialized = false;

            if (mode === PropertyProviderModes.SingleProperty) {
                if (activeProperty && activeProperty.id) {
                    nextIsInitialized = true;
                }
            } else if (mode === PropertyProviderModes.MultiProperty) {
                if (propertyGroup && allProperties.length > 0) {
                    nextIsInitialized = true;
                }
            }

            if (nextIsInitialized) {
                resolveAndSetNextActiveProperty();

                setIsInitialized(true);
            }
        }
    }, [
        hasStorageBeenRecovered,
        isPropertyGroupFetched,
        mode,
        activeProperty,
        activePropertyId,
        propertyGroup,
        propertyGroup.properties,
        isInitialized,
        allProperties.length,
        resolveAndSetNextActiveProperty,
    ]);

    return (
        <PropertyContext.Provider
            value={{
                firstStep,
                propertyGroupId,
                propertyGroup,
                activeProperty,
                activePropertyId,
                isActivePropertyLoading,
                isActivePropertyFetched,
                forceIbeReset,
                allProperties,
                forceActivePropertyIdTo: setActivePropertyId,
                changeActivePropertyId,
                propertyResolverMode: mode,
            }}
        >
            {isInitialized ? children : null}
        </PropertyContext.Provider>
    );
};
